/**
 * CommentComposer (DESIGN.md "Comments sit in parts"): the foot of the
 * Comments tab. It writes on the part the page has open, the concept or the
 * creative brief. Every comment is for everyone, so there is no audience
 * choice. Once the idea and the brief are approved it gives way to one muted
 * line (`closedLine`): no feedback after approval.
 */
import { forwardRef, useImperativeHandle, useRef, useState, type ReactNode } from "react";
import { ArrowUp, X } from "lucide-react";
import { Avatar } from "./CommentThread";
import type { CommentSource } from "./commentModel";
import MentionField, { mentionsIn, type MentionFieldHandle, type MentionPick } from "./MentionField";
import type { MentionPerson } from "./useMentionPeople";
import "./comments.css";

export interface CommentComposerProps {
  source: CommentSource;
  /** Your name, for the Avatar. */
  authorName: string;
  clientName: string | null;
  /** Brief only, and only when true: "{client} sees brief comments". */
  sharedNote?: string | null;
  /** Why the field is off right now, in one line; null when it is on. */
  disabledReason?: string | null;
  /**
   * Comments are closed for good (the idea and the brief are approved): the
   * whole composer is replaced by this one muted line.
   */
  closedLine?: string | null;
  /**
   * The part of the page the comment is on ("the hook"), as a chip before the
   * field. The anchor itself is not sent until backend #128.
   */
  part?: { label: string; onClear: () => void } | null;
  /** Phones: 16px text so iOS does not zoom, and no keyboard hints. */
  phone?: boolean;
  /**
   * The client's page (DESIGN.md "Comments sit in parts"): its own
   * placeholder and keyboard line, and a field that grows line by line to six
   * lines.
   */
  audience?: "team" | "client";
  placeholder?: string;
  /** The keyboard line under the field, in place of Enter posts. */
  keysHint?: ReactNode;
  /** An id for the field, so a shortcut can focus it. */
  inputId?: string;
  /** Who the field can @mention (the team's page); none turns the picker off. */
  people?: readonly MentionPerson[];
  onPost: (body: string, mentions: MentionPick[]) => void;
}

export interface CommentComposerHandle {
  focus: () => void;
}

const CommentComposer = forwardRef<CommentComposerHandle, CommentComposerProps>(function CommentComposer({
  source,
  authorName,
  sharedNote,
  disabledReason,
  closedLine,
  part,
  phone = false,
  audience = "team",
  placeholder: placeholderOverride,
  keysHint,
  inputId,
  people = [],
  onPost,
}, ref) {
  const field = useRef<MentionFieldHandle>(null);
  useImperativeHandle(ref, () => ({ focus: () => field.current?.focus() }), []);
  // One draft per part, so switching Concept and Creative brief keeps both.
  // The composer is keyed by post, so a new post starts empty.
  const [drafts, setDrafts] = useState<Record<CommentSource, string>>({ concept: "", brief: "" });
  const draft = drafts[source];
  const setDraft = (value: string) => setDrafts((all) => ({ ...all, [source]: value }));
  const [picked, setPicked] = useState<MentionPick[]>([]);
  const disabled = !!disabledReason;
  const ready = !disabled && !!draft.trim();
  const client = audience === "client";
  const placeholder =
    placeholderOverride ?? "Comment on any part of the page…";

  const post = () => {
    const body = draft.trim();
    if (!body || disabled) return;
    onPost(body, mentionsIn(body, picked));
    setDraft("");
    setPicked([]);
    part?.onClear();
  };

  // No feedback after approval: the field stays, holding one muted line.
  if (closedLine) {
    return (
      <div className="co-cmt-composer" data-closed="true" data-phone={phone || undefined}>
        <div className="co-cmt-field co-glass-control" data-closed="true">
          <p className="co-cmt-closed">{closedLine}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="co-cmt-composer" data-phone={phone || undefined} data-client={client || undefined}>
      {part && !disabled ? (
        <span className="co-cmt-part">
          On: {part.label}
          <button type="button" aria-label={`Not on ${part.label}`} title="Remove" onClick={part.onClear}>
            <X strokeWidth={2} aria-hidden="true" />
          </button>
        </span>
      ) : null}
      <div className="co-cmt-field co-glass-control" data-disabled={disabled || undefined}>
        <Avatar name={authorName} />
        <MentionField
          ref={field}
          id={inputId}
          className="co-cmt-textarea"
          value={draft}
          disabled={disabled}
          ariaLabel={placeholder.replace("…", "")}
          placeholder={placeholder}
          people={people}
          onPick={(pick) => setPicked((all) => [...all, pick])}
          onChange={setDraft}
          onSubmit={post}
          grow={client}
        />
        <button
          type="button"
          className="co-cmt-send"
          data-ready={ready || undefined}
          disabled={!ready}
          aria-label="Post comment"
          onClick={post}
        >
          <ArrowUp strokeWidth={2} aria-hidden="true" />
        </button>
      </div>
      {disabledReason || (source === "brief" && sharedNote) || !phone ? (
        <p className="co-cmt-hint">
          {disabledReason ? (
            <span className="co-cmt-note">{disabledReason}</span>
          ) : (
            <>
              {source === "brief" && sharedNote ? <span className="co-cmt-note">{sharedNote}</span> : null}
              {phone ? null : keysHint ? (
                <span>{keysHint}</span>
              ) : (
                <span>Enter posts · shift+enter new line · c to comment</span>
              )}
            </>
          )}
        </p>
      ) : null}
    </div>
  );
});

export default CommentComposer;
